import axios from 'axios';
import axiosClient from '../../../../utils/axios';
import { resolve } from '../../../../api/endPoints';
import { generateFirebaseUploadUrl } from '../../../../redux/slices/lomavisCreator/utils';
import { UPLOAD_TYPE_POST_MEDIA } from '../../../../config/constants';
import { inferMimeFromFilename } from './cloudPickerFileUtils';
import type { CloudPickerUploadDescriptor } from './cloudPickers/types';

interface UploadCloudFileViaBackendParams {
  descriptor: CloudPickerUploadDescriptor;
  name?: string;
  size?: number;
  mimeType?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  locationUuid: string;
  cloudUuid: string;
  signal?: AbortSignal;
}

interface CloudFileBackendMetadata {
  contentType?: string;
  content_type?: string;
  size?: number;
  width?: number;
  height?: number;
  original_width?: number;
  original_height?: number;
  duration_in_ms?: number;
}

const fetchCloudFileBackendMetadata = async (
  descriptor: CloudPickerUploadDescriptor,
  signal?: AbortSignal
): Promise<CloudFileBackendMetadata> => {
  try {
    const url = resolve('media_metadata', {}, {});
    const response = await axiosClient.post(
      url,
      {
        url: encodeURIComponent(descriptor.url),
        ...(descriptor.accessToken
          ? { accessToken: descriptor.accessToken }
          : {})
      },
      { signal }
    );
    const signedUrl = response.data?.signed_url;
    if (!signedUrl) {
      return {};
    }
    const signedResponse = await fetch(signedUrl, { signal });
    if (!signedResponse.ok) {
      return {};
    }
    return (await signedResponse.json()) as CloudFileBackendMetadata;
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    console.warn('Cloud file metadata lookup failed.', error);
    return {};
  }
};

export const uploadCloudFileViaBackend = async ({
  descriptor,
  name,
  size,
  mimeType,
  width,
  height,
  durationMs,
  locationUuid,
  cloudUuid,
  signal
}: UploadCloudFileViaBackendParams) => {
  const needsBackendMetadata =
    !width || !height || !mimeType || size === undefined;
  const backendMetadata = needsBackendMetadata
    ? await fetchCloudFileBackendMetadata(descriptor, signal)
    : {};

  const resolvedMimeType =
    mimeType ||
    backendMetadata.contentType ||
    backendMetadata.content_type ||
    inferMimeFromFilename(name);
  if (!resolvedMimeType) {
    throw new Error(
      `Unable to determine content type for cloud file "${name || 'unknown'}".`
    );
  }

  const resolvedSize = size ?? backendMetadata.size ?? 0;
  const resolvedWidth =
    width ?? backendMetadata.width ?? backendMetadata.original_width;
  const resolvedHeight =
    height ?? backendMetadata.height ?? backendMetadata.original_height;
  const resolvedDuration = durationMs ?? backendMetadata.duration_in_ms;

  const uploadPreset = await generateFirebaseUploadUrl(
    {
      content_type: resolvedMimeType,
      filesize_in_bytes: resolvedSize,
      filename: name || `cloud-file-${Date.now()}`,
      location_uuid: locationUuid,
      cloud_uuid: cloudUuid,
      width: resolvedWidth,
      height: resolvedHeight,
      duration_in_ms: resolvedDuration,
      upload_type: UPLOAD_TYPE_POST_MEDIA
    },
    {},
    () => {
      console.error('Failed to generate upload preset for cloud file.');
    }
  );

  if (!uploadPreset) {
    throw new Error('Failed to generate upload preset for cloud file.');
  }

  const mediaDownloadUrl = resolve('media_download_url', {}, {});
  const uploadResponse = await axiosClient.post(
    mediaDownloadUrl,
    {
      url: descriptor.url,
      uploadPreset,
      ...(descriptor.accessToken
        ? { accessToken: descriptor.accessToken }
        : {})
    },
    { signal }
  );

  const downloadEndpoint = uploadResponse.data?.endpoint;
  if (!downloadEndpoint) {
    throw new Error(
      'Backend did not return a download endpoint for the cloud file.'
    );
  }

  await axios.post(downloadEndpoint, uploadResponse.data, { signal });
  await axiosClient.put(
    uploadPreset.confirm_url,
    { success: true },
    { signal }
  );

  return uploadPreset;
};
